// Engagement_Dashboard.utils.ts
import Highcharts from "highcharts/highstock";

Highcharts.setOptions({
  lang: {
    thousandsSep: ',',
  },
});

/**
 * Shared types
 */
export type SelectOption = { label: string; value: string };

export type DashboardView = "Executive" | "Operations";

export type DashboardFilters = {
  businessUnits?: string[]; // "Line of Service" UI, maps to BusinessUnit in backend
  offices?: string[];
  partners?: string[];
  fiscalYears?: string[]; // if backend supports FY, else keep for UI only
};

export type DashboardFiltersPayload = {
  BusinessUnit?: string[]; // backend expects list of strings OR omit
  Office?: string[];
  PrimaryPartner?: string[];
  FiscalYear?: string[]; // optional, only include if your API supports it
};

export type ApiContext<T> = {
  success?: boolean;
  message?: string;
  data?: T;
  errors?: Record<string, string>;
};

export type ApprovalStatusRow = {
  ApprovalStatus: string;
  TotalCount: number;
};

export type SignatureStatusRow = {
  SignatureStatus: string;
  TotalCount: number;
};

export type OfficeStatusRow = {
  Office: string;
  ApprovalStatus: string;
  TotalCount: number;
};

export type PartnerStatusRow = {
  PrimaryPartner: string;
  ApprovalStatus: string;
  TotalCount: number;
};

export type PartnerReturnTypeRow = {
  PrimaryPartner: string;
  ReturnType: string;
  TotalCount: number;
  ForecastAmount: number;
  TotalPrimaryPartnerCount?: number;
  TotalPrimaryPartnerAmount?: number;
};

export type OfficeForecastAmountRow = {
  Office: string;
  ReturnType: string;
  ELCount: number;
  ForecastAmount: number;
  TotalOfficeCount?: number;
  TotalOfficeAmount?: number;
};

export type ReturnTypeSummaryRow = {
  ReturnType: string;
  TotalCount: number;
  ForecastAmount: number;
};

export type LineOfServiceForecastAmountRow = {
  LineOfService: string;
  ForecastAmount: number;
  ELCount?: number;
};

export type DropdownStoreState = {
  purposeOptions?: Array<{ label: string; value: string; code?: string }>; // Line of Service
  officeLocationOptions?: Array<{ label: string; value: string; id?: string }>;
  primaryPartnerOptions?: Array<{ label: string; value: string; id?: string; location?: string }>;
};

export type ThemeColors = {
  primary: string;
  tertiary: string;
  palette: string[];
};

/**
 * Constants
 */
export const ALL_OPTION: SelectOption = { label: "All", value: "All" };

export const STATUS_COLOR_MAP: Record<string, string> = {
  // approval statuses
  Draft: "#FFD54F",
  "Pending Approval": "#1976D2",
  "Partially Approved": "#FF7F00",
  Rejected: "#D32F2F",
  Approved: "#388E3C",

  // signature statuses
  "Out For Signature": "#003f5c",
  "Delivery Failed": "#D32F2F",
  "Partially Signed": "#58508d",
  "Processing": "#bc5090",
  "Awaiting WetSign": "#ffa600",
  "None": "#bdbdbd",
  "E-Signed": "#3adb42",
  "ESigned": "#388E3C",
  "Signed": "#388E3C",
  "WetSign Completed": "#52caef",
  "Manually Signed": "#508bc2",
  "OutForDelivery": "#571771",
  "Failed": "#c62828b0",
  "Recalled": "#d9c706",
  "Declined": "#e58f73",
  "Deleted": "#B45309",
};

export const RETURN_TYPE_COLOR_MAP: Record<string, string> = {
  "1040": "#4e79a7",
  "1040-NR": "#f28e2b",
  "1041": "#e15759",
  "1065": "#76b7b2",
  "1120": "#59a14f",
  "1120-S": "#edc948",
  "1120-F": "#b07aa1",
  "1120-H": "#ff9da7",
  "5500": "#9c755f",
  "706": "#bab0ab",
  "709": "#86bc86",
  "990": "#1f77b4",
  "990-PF": "#d62728",
  "Unknown": "#7f7f7f",
};

export const LINE_OF_SERVICE_COLOR_MAP: Record<string, string> = {
  tax: "#4e79a7",
  assurance: "#f28e2b",
  "business management": "#39d1ff",
  "client accounting services": "#76b7b2",
  advisory: "#59a14f",
  "no line of service": "#f5f56b",
};

export const FIXED_APPROVAL_STATUS_ORDER = [
  "Draft",
  "Pending Approval",
  "Partially Approved",
  "Rejected",
  "Approved",
] as const;

/**
 * Helpers
 */
export const buildFiltersPayload = (filters: DashboardFilters): DashboardFiltersPayload => {
  const payload: DashboardFiltersPayload = {};

  if (filters.businessUnits?.length) payload.BusinessUnit = filters.businessUnits;
  if (filters.offices?.length) payload.Office = filters.offices;
  if (filters.partners?.length) payload.PrimaryPartner = filters.partners;
  if (filters.fiscalYears?.length) payload.FiscalYear = filters.fiscalYears;

  return payload;
};

export const selectOptionsFromStrings = (values: string[]): SelectOption[] =>
  values.map((v) => ({ label: v, value: v }));

export const optionOrAll = (value?: string | null): SelectOption =>
  value ? { label: value, value } : ALL_OPTION;

export const pickDefaultBusinessUnit = (
  store: DropdownStoreState | null | undefined
): SelectOption => {
  const first = store?.purposeOptions?.[0]?.value;
  return first ? { label: store!.purposeOptions![0].label, value: first } : ALL_OPTION;
};

export const pickDefaultOffice = (
  store: DropdownStoreState | null | undefined,
  myOffice?: string | null
): SelectOption => {
  const officeValue =
    myOffice ||
    store?.officeLocationOptions?.find((o) => o.value)?.value ||
    store?.officeLocationOptions?.[0]?.value;

  if (!officeValue) return ALL_OPTION;

  const label =
    store?.officeLocationOptions?.find((o) => o.value === officeValue)?.label || officeValue;

  return { label, value: officeValue };
};

export const pickDefaultPartner = (
  store: DropdownStoreState | null | undefined
): SelectOption => {
  const first = store?.primaryPartnerOptions?.[0]?.value;
  const label = store?.primaryPartnerOptions?.[0]?.label;
  return first ? { label: label || first, value: first } : ALL_OPTION;
};

/**
 * If you need "current Tax year" default:
 * - Define the FY naming convention you use (FY2026 etc)
 * - This assumes US tax year aligned to calendar year.
 * Change logic if you use fiscal year Jul-Jun.
 */
export const getCurrentTaxYearValue = (): string => {
  const now = new Date();
  const year = now.getFullYear();
  return `FY${year}`;
};

export const safeNumber = (n: unknown): number => (typeof n === "number" && Number.isFinite(n) ? n : 0);

export const getReturnTypeShortLabel = (returnType: string): string => {
  const value = String(returnType || "").trim();

  if (!value) return "Unknown";

  if (value.startsWith("1040-NR")) return "1040-NR";
  if (value.startsWith("1040")) return "1040";
  if (value.startsWith("1041")) return "1041";
  if (value.startsWith("1065")) return "1065";
  if (value.startsWith("1120-S")) return "1120-S";
  if (value.startsWith("1120-F")) return "1120-F";
  if (value.startsWith("1120-H")) return "1120-H";
  if (value.startsWith("1120")) return "1120";
  if (value.startsWith("5500")) return "5500";
  if (value.startsWith("706")) return "706";
  if (value.startsWith("709")) return "709";
  if (value.startsWith("990-PF")) return "990-PF";
  if (value.startsWith("990")) return "990";

  return value;
};

export const safeCurrencyNumber = (n: unknown): number => {
  if (typeof n === "number" && Number.isFinite(n)) return n;

  const parsed = Number(
    String(n ?? "")
      .replace(/\$/g, "")
      .replace(/,/g, "")
      .trim()
  );

  return Number.isFinite(parsed) ? parsed : 0;
};

export const formatCompactCurrency = (value: number): string => {
  const amount = safeCurrencyNumber(value);

  if (amount >= 1_000_000) {
    return `${(amount / 1_000_000).toFixed(2)}M`;
  }

  if (amount >= 100_000) {
    return `${(amount / 1_000).toFixed(2)}K`;
  }

  return Highcharts.numberFormat(amount, 0);
};

export const formatChartCurrencyCompact = (value: number): string => {
  const amount = safeCurrencyNumber(value);

  if (amount >= 1_000_000) {
    return `$${(amount / 1_000_000).toFixed(2)} M`;
  }

  if (amount >= 10_000) {
    return `$${(amount / 1_000).toFixed(1)} K`;
  }

  return `$${Highcharts.numberFormat(amount, 0)}`;
};

export const formatKpiNumber = (value: number): string => {
  return Highcharts.numberFormat(safeNumber(value), 0);
};

export const getThemeColor = (
  palette: string[] | undefined,
  index: number,
  fallback = "#7f7f7f"
): string => {
  if (!palette || palette.length === 0) return fallback;
  return palette[index % palette.length] || fallback;
};

export const buildStablePaletteMap = (
  keys: string[],
  palette: string[] | undefined,
  fallback = "#7f7f7f"
): Record<string, string> => {
  const uniqueKeys = Array.from(new Set(keys.filter(Boolean))).sort();

  return uniqueKeys.reduce<Record<string, string>>((acc, key, index) => {
    acc[key] =
      palette && palette.length > 0
        ? palette[index % palette.length] || fallback
        : fallback;
    return acc;
  }, {});
};

export const getPersistentPaletteMap = (
  storageKey: string,
  keys: string[],
  palette: string[] | undefined,
  fallback = "#7f7f7f"
): Record<string, string> => {
  if (!palette || palette.length === 0) {
    return keys.reduce<Record<string, string>>((acc, key) => {
      acc[key] = fallback;
      return acc;
    }, {});
  }

  let savedMap: Record<string, string> = {};

  try {
    const raw = localStorage.getItem(storageKey);
    savedMap = raw ? JSON.parse(raw) : {};
  } catch {
    savedMap = {};
  }

  const uniqueKeys = Array.from(new Set(keys.filter(Boolean)));

  uniqueKeys.forEach((key) => {
    if (!savedMap[key]) {
      const usedColors = new Set(Object.values(savedMap));
      const nextColor =
        palette.find((c) => !usedColors.has(c)) ||
        palette[Object.keys(savedMap).length % palette.length] ||
        fallback;

      savedMap[key] = nextColor;
    }
  });

  try {
    localStorage.setItem(storageKey, JSON.stringify(savedMap));
  } catch {
    // no-op
  }

  return savedMap;
};

/**
 * Highcharts - shared defaults
 */
export const chartDefaults: Highcharts.Options = {
  lang: { noData: "No data available" },
  noData: {
    style: {
      fontWeight: "bold",
      fontSize: "16px",
      color: "#6E6E6E",
    },
  },
};

type ColumnSeries = Highcharts.SeriesColumnOptions;
type BarSeries = Highcharts.SeriesBarOptions;
type PieSeries = Highcharts.SeriesPieOptions;

// Some common styling for charts
type ScrollbarOptions = {
  enabled: boolean;
  height?: number;
  margin?: number;
  minWidth?: number;
};

const buildStandardScrollbar = ({
  enabled,
  height = 10,
  margin = 8,
  minWidth = 30,
}: ScrollbarOptions): any => ({
  enabled,
  showFull: false,

  // thumb
  barBorderRadius: 6,
  barBorderWidth: 0,
  barBackgroundColor: "#6B7280",
  barBorderColor: "#6B7280",
  rifleColor: "#F9FAFB",

  // track
  trackBorderRadius: 6,
  trackBorderWidth: 0,
  trackBackgroundColor: "#E5E7EB",
  trackBorderColor: "#E5E7EB",

  // buttons
  buttonBorderWidth: 0,
  buttonArrowColor: "#4B5563",
  buttonBackgroundColor: "#D1D5DB",
  buttonBorderColor: "#D1D5DB",

  height,
  margin,
  minWidth,
  mouseWheelEnabled: true,
});

type BindWheelScrollOptions = {
  chart: Highcharts.Chart;
  totalItems: number;
  visibleCountDefault?: number;
  bindingKey: string;
};

const bindCategoryAxisWheelScroll = ({
  chart,
  totalItems,
  visibleCountDefault = 4,
  bindingKey,
}: BindWheelScrollOptions) => {
  const axis = chart.xAxis[0];
  const container = chart.container as any;

  if (container[bindingKey]) return;
  container[bindingKey] = true;

  container.addEventListener(
    "wheel",
    function (e: WheelEvent) {
      if (totalItems <= visibleCountDefault + 1) return;

      e.preventDefault();

      const currentMin = Math.max(0, Math.floor(Number(axis.min ?? 0)));
      const currentMax = Math.min(
        totalItems - 1,
        Math.ceil(Number(axis.max ?? visibleCountDefault))
      );

      const visibleCount = currentMax - currentMin + 1;
      const step = 1;

      let nextMin = currentMin;

      if (e.deltaY > 0) {
        nextMin = Math.min(totalItems - visibleCount, currentMin + step);
      } else {
        nextMin = Math.max(0, currentMin - step);
      }

      const nextMax = nextMin + visibleCount - 1;
      axis.setExtremes(nextMin, nextMax, true, false);
    },
    { passive: false }
  );
};

const buildChartTooltipCard = (title: string, lines: string[]) => `
  <div style="
    background: #ffffff;
    border: 1px solid #d9d9d9;
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.12);
    padding: 8px 10px;
    color: #333333;
    font-size: 12px;
  ">
    <div style="font-weight: 600; margin-bottom: 4px;">${title}</div>
    ${lines.map((line) => `<div>${line}</div>`).join("")}
  </div>
`;

/**
 * Totals / KPIs
 */
export const getTotalCountFromRows = (rows: Array<{ TotalCount: number }>): number =>
  rows.reduce((sum, r) => sum + safeNumber(r.TotalCount), 0);

/**
 * Chart builders
 */

/**
 * Approval Status Distribution (column)
 * backend rows: [{ ApprovalStatus, TotalCount }]
 */
export const buildApprovalStatusBarOptions = (rows: ApprovalStatusRow[]): Highcharts.Options => {
  const countByStatus = rows.reduce<Record<string, number>>((acc, r) => {
    acc[r.ApprovalStatus] = (acc[r.ApprovalStatus] || 0) + safeNumber(r.TotalCount);
    return acc;
  }, {});

  const series: ColumnSeries[] = [
    {
      type: "column",
      name: "Count",
      data: [...FIXED_APPROVAL_STATUS_ORDER].map((status) => ({
        name: status,
        y: countByStatus[status] ?? 0,
        color: STATUS_COLOR_MAP[status],
      })),
    },
  ];

  return {
    ...chartDefaults,
    chart: { type: "column" },
    title: { text: "EL Approval Status Distribution" },
    xAxis: {
      categories: [...FIXED_APPROVAL_STATUS_ORDER],
      title: { text: "Status" },
      labels: { style: { fontSize: "12px" } },
    },
    yAxis: {
      min: 0,
      title: { text: "Total Count" },
      allowDecimals: false,
    },
    legend: { enabled: false },
    tooltip: { pointFormat: "<b>{point.y}</b> ELs" },
    plotOptions: { column: { pointWidth: 30 } },
    series,
  } satisfies Highcharts.Options;
};

/**
 * Signature Status Distribution (pie)
 * backend rows: [{ SignatureStatus, TotalCount }]
 */
export const buildSignatureStatusPieOptions = (
  rows: SignatureStatusRow[],
  statuses?: readonly string[]
): { options: Highcharts.Options; total: number } => {
  const filteredRows = statuses?.length
    ? rows.filter((r) => statuses.includes(r.SignatureStatus))
    : rows;

  const total = getTotalCountFromRows(filteredRows);

  const minimumVisibleShare = 0.01;

  const series: PieSeries[] = [
    {
      type: "pie",
      name: "EL Count",
      data: filteredRows.map((r) => {
        const actualCount = safeNumber(r.TotalCount);
        const actualShare = total > 0 ? actualCount / total : 0;

        return {
          name: r.SignatureStatus,
          y:
            actualCount > 0 && actualShare < minimumVisibleShare
              ? total * minimumVisibleShare
              : actualCount,
          color: STATUS_COLOR_MAP[r.SignatureStatus] || "#7f7f7f",
          custom: {
            actualCount,
            actualShare,
          },
        };
      }),
    },
  ];

  return {
    total,
    options: {
      ...chartDefaults,
      chart: { type: "pie" },
      title: {
        text: "EL Pending Signature Status Distribution",
      },
      tooltip: {
        useHTML: true,
        borderWidth: 0,
        backgroundColor: "transparent",
        shadow: false,
        formatter: function () {
          try {
            const point = this.point as any;
            const status = String(point?.name ?? "");
            const actualCount = Number(point?.custom?.actualCount ?? point?.y ?? 0);
            const percentage = Number(
              point?.custom?.actualShare != null
                ? point.custom.actualShare * 100
                : point?.percentage ?? 0
            );

            return buildChartTooltipCard(status, [
              `ELs: <b>${actualCount}</b>`,
              `Share: <b>${Highcharts.numberFormat(percentage, 1)}%</b>`,
            ]);
          } catch (e) {
            return "";
          }
        },
      },
      plotOptions: {
        pie: {
          allowPointSelect: true,
          cursor: "pointer",
          size: "80%",
          borderWidth: 1,
          borderColor: "#ffffff",
          dataLabels: {
            enabled: true,
            formatter: function () {
              const point = this.point as any;
              const actualCount = Number(point?.custom?.actualCount ?? point?.y ?? 0);
              return `<b>${point.name}</b>: ${actualCount}`;
            },
          },
          showInLegend: true,
        },
      },
      legend: {
        enabled: true,
        layout: "horizontal",
        align: "center",
        verticalAlign: "bottom",
      },
      series,
    } satisfies Highcharts.Options,
  };
};

/**
 * Pending vs Completed EL Status (donut)
 * backend rows: [{ SignatureStatus, TotalCount }]
 * groups signature statuses into Pending and Completed buckets
 */
export const buildPendingCompletedDonutOptions = (
  rows: SignatureStatusRow[],
  pendingStatuses: readonly string[],
  completedStatuses: readonly string[],
  deletedStatuses: readonly string[]
): {
  options: Highcharts.Options;
  pendingTotal: number;
  completedTotal: number;
  deletedTotal: number;
  total: number;
} => {
  const getStatusCount = (status: string) =>
    rows
      .filter(
        (r) =>
          String(r.SignatureStatus || "").trim().toLowerCase() ===
          status.trim().toLowerCase()
      )
      .reduce((sum, r) => sum + safeNumber(r.TotalCount), 0);

  const pendingBreakdown = pendingStatuses.map((status) => ({
    status,
    count: getStatusCount(status),
  }));

  const completedBreakdown = completedStatuses.map((status) => ({
    status,
    count: getStatusCount(status),
  }));

  const deletedBreakdown = deletedStatuses.map((status) => ({
    status,
    count: getStatusCount(status),
  }));

  const pendingTotal = pendingBreakdown.reduce(
    (sum, item) => sum + item.count,
    0
  );

  const completedTotal = completedBreakdown.reduce(
    (sum, item) => sum + item.count,
    0
  );

  const deletedTotal = deletedBreakdown.reduce(
    (sum, item) => sum + item.count,
    0
  );

  const total = pendingTotal + completedTotal + deletedTotal;

  const minimumVisibleShare = 0.01;

  const series: PieSeries[] = [
    {
      type: "pie",
      name: "EL Count",
      innerSize: "60%",
      size: "82%",
      data: [
        {
          name: "Pending",
          y:
            pendingTotal > 0 && total > 0 && pendingTotal / total < minimumVisibleShare
              ? total * minimumVisibleShare
              : pendingTotal,
          color: STATUS_COLOR_MAP["Pending Approval"],
          custom: {
            actualCount: pendingTotal,
            breakdown: pendingBreakdown,
          },
        },
        {
          name: "Completed",
          y:
            completedTotal > 0 && total > 0 && completedTotal / total < minimumVisibleShare
              ? total * minimumVisibleShare
              : completedTotal,
          color: STATUS_COLOR_MAP["Approved"],
          custom: {
            actualCount: completedTotal,
            breakdown: completedBreakdown,
          },
        },
        {
          name: "Deleted",
          y:
            deletedTotal > 0 && total > 0 && deletedTotal / total < minimumVisibleShare
              ? total * minimumVisibleShare
              : deletedTotal,
          color: STATUS_COLOR_MAP["Deleted"],
          custom: {
            actualCount: deletedTotal,
            breakdown: deletedBreakdown,
          },
        },
      ],
    },
  ];

  return {
    pendingTotal,
    completedTotal,
    deletedTotal,
    total,

    options: {
      ...chartDefaults,

      chart: {
        type: "pie",
      },

      title: {
        text: "EL Completion Status Distribution",
      },

      tooltip: {
        useHTML: true,
        borderWidth: 0,
        backgroundColor: "transparent",
        shadow: false,

        formatter: function () {
          const point = this.point as any;

          const name = String(point?.name ?? "");
          const totalCount = Number(
            point?.custom?.actualCount ?? point?.y ?? 0
          );

          const breakdown = Array.isArray(point?.custom?.breakdown)
            ? point.custom.breakdown
            : [];

          const breakdownLines = breakdown.map(
            (item: any) =>
              `${item.status}: <b>${Highcharts.numberFormat(
                Number(item.count ?? 0),
                0
              )}</b>`
          );

          return buildChartTooltipCard(name, [
            `Total ELs: <b>${Highcharts.numberFormat(totalCount, 0)}</b>`,
            ...breakdownLines,
          ]);
        },
      },

      plotOptions: {
        pie: {
          borderWidth: 1,
          borderColor: "#ffffff",

          dataLabels: {
            enabled: true,
            formatter: function () {
              const point = this.point as any;
              const actualCount = Number(
                point?.custom?.actualCount ?? point?.y ?? 0
              );

              return `<b>${point.name}</b>: ${Highcharts.numberFormat(
                actualCount,
                0
              )}`;
            },
          },

          showInLegend: true,
        },
      },

      legend: {
        enabled: true,
        layout: "horizontal",
        align: "center",
        verticalAlign: "bottom",
      },

      series,
    } satisfies Highcharts.Options,
  };
};


/**
 * Partner x Status stacked bar (bar)
 * backend rows: [{ PrimaryPartner, ApprovalStatus, TotalCount }]
 */
export const buildPartnerStackedBarOptions = (
  rows: PartnerStatusRow[],
  theme?: ThemeColors,
  statuses: readonly string[] = FIXED_APPROVAL_STATUS_ORDER
): Highcharts.Options => {
  const partners = Array.from(new Set(rows.map((r) => r.PrimaryPartner))).filter(Boolean);

  const totals = partners
    .map((partner) => ({
      partner,
      total: rows
        .filter(
          (r) =>
            r.PrimaryPartner === partner &&
            statuses.includes(r.ApprovalStatus as any)
        )
        .reduce((sum, r) => sum + safeNumber(r.TotalCount), 0),
    }))
    .sort((a, b) => b.total - a.total)
    .map((x) => x.partner);

  const getVal = (partner: string, status: string) =>
    rows
      .filter((r) => r.PrimaryPartner === partner && r.ApprovalStatus === status)
      .reduce((sum, r) => sum + safeNumber(r.TotalCount), 0);

  const series: BarSeries[] = [...statuses].map((status) => ({
    type: "bar",
    name: status,
    data: totals.map((partner) => getVal(partner, status)),
    color: STATUS_COLOR_MAP[status] || undefined,
  }));

  return {
    ...chartDefaults,
    chart: {
      type: "bar",
      marginRight: 50,
      spacingBottom: 20,
      plotBorderColor: "#eef0f1",
      plotBorderWidth: 1,
      events: {
        load: function () {
          bindCategoryAxisWheelScroll({
            chart: this,
            totalItems: totals.length,
            visibleCountDefault: 4,
            bindingKey: "__wheelBoundELPartner",
          });
        },
      },
    },
    title: {
      text:
        statuses.length === 1
          ? "EL Distribution by Primary Partner (Approved)"
          : "EL Distribution by Primary Partner",
    },
    xAxis: {
      categories: totals,
      title: { text: "Primary Partner" },
      gridLineWidth: 0,
      lineWidth: 0,
      tickWidth: 0,
      min: 0,
      max: Math.min(4, Math.max(totals.length - 1, 0)),
      scrollbar: buildStandardScrollbar({
        enabled: totals.length > 5,
        height: 10,
        margin: 8,
        minWidth: 30,
      }),
    },
    yAxis: {
      min: 0,
      endOnTick: false,
      maxPadding: 0.12,
      gridLineWidth: 1,
      title: { text: "Total Count" },
      stackLabels: {
        enabled: true,
        crop: false,
      },
    },
    tooltip: {
      useHTML: true,
      borderWidth: 0,
      backgroundColor: "transparent",
      shadow: false,
      formatter: function () {
        try {
          const point = this.point as any;
          const partner = String(this.x ?? "");
          const status = String(this.series.name ?? "");
          const count = Number(this.y ?? 0);
          const total = Number(point?.stackTotal ?? 0);

          return buildChartTooltipCard(partner, [
            `${status}: <b>${count}</b>`,
            `Total: <b>${total}</b>`,
          ]);
        } catch (e) {
          return "";
        }
      },
    },
    plotOptions: {
      bar: {
        stacking: "normal",
        dataLabels: { enabled: false },
        pointWidth: 26,
        groupPadding: 0.12,
        pointPadding: 0.04,
        borderWidth: 1,
        borderColor: "#ffffff",
      },
    },
    legend: {
      enabled: true,
      align: "center",
      verticalAlign: "bottom",
      margin: 12,
      padding: 8,
    },
    series,
  } satisfies Highcharts.Options;
};


/**
 * Partner x Return Type stacked bar (bar)
 * backend rows: [{ PrimaryPartner, ReturnType, TotalCount }]
 * - Groups EL count by Primary Partner and Return Type
 * - Uses short labels (1040, 1120, etc.) for better readability in legend
 */
export const buildPartnerReturnTypeStackedBarOptions = (
  rows: PartnerReturnTypeRow[],
  theme?: ThemeColors,
): Highcharts.Options => {
  const partners = Array.from(new Set(rows.map((r) => r.PrimaryPartner))).filter(Boolean);

  const partnerTotalsMap = partners.reduce<
    Record<string, { count: number; amount: number }>
  >((acc, partner) => {
    const partnerRows = rows.filter((r) => r.PrimaryPartner === partner);
    acc[partner] = {
      count: partnerRows.reduce((sum, r) => sum + safeNumber(r.TotalCount), 0),
      amount: partnerRows.reduce(
        (sum, r) => sum + safeCurrencyNumber(r.ForecastAmount),
        0
      ),
    };
    return acc;
  }, {});

  const totals = partners
    .map((p) => ({
      p,
      totalCount: partnerTotalsMap[p]?.count ?? 0,
      totalAmount: partnerTotalsMap[p]?.amount ?? 0,
    }))
    .sort((a, b) => {
      if (b.totalAmount !== a.totalAmount) return b.totalAmount - a.totalAmount;
      return b.totalCount - a.totalCount;
    })
    .map((x) => x.p);

  const setVisibleYAxisExtremes = (chart: Highcharts.Chart) => {
    try {
      const categoryAxis = chart.xAxis[0];
      const valueAxis = chart.yAxis[0];

      const minIndex = Math.max(0, Math.floor(Number(categoryAxis.min ?? 0)));
      const maxIndex = Math.min(
        Math.max(totals.length - 1, 0),
        Math.ceil(Number(categoryAxis.max ?? totals.length - 1))
      );

      let visibleMax = 0;

      for (let i = minIndex; i <= maxIndex; i++) {
        let stackTotal = 0;

        chart.series.forEach((series: any) => {
          if (series.visible) {
            const point = series.data?.[i];
            stackTotal += Number(point?.y ?? 0);
          }
        });

        visibleMax = Math.max(visibleMax, stackTotal);
      }

      const paddedMax = visibleMax > 0 ? visibleMax * 1.30 : 1;
      valueAxis.setExtremes(0, paddedMax, false, false);
      chart.redraw(false);
    } catch (e) {
      // no-op
    }
  };

  const returnTypes = Array.from(new Set(rows.map((r) => r.ReturnType))).filter(Boolean);

  const orderedReturnTypes = [
    "1040 Individual",
    "1040-NR Individual",
    "1041 Fiduciary",
    "1065 Partnership",
    "1120 Corporation",
    "1120-S Corporation",
    "1120-F Corporation",
    "1120-H Corporation",
    "5500 Employee Benefit Plan",
    "706 Estate",
    "709 Gift",
    "990 Exempt Organization",
    "990-PF Private Foundation",
  ].filter((rt) => returnTypes.includes(rt));

  const remainingReturnTypes = returnTypes.filter(
    (rt) => !orderedReturnTypes.includes(rt)
  );

  const finalReturnTypes = [...orderedReturnTypes, ...remainingReturnTypes];

  const returnTypeKeys = finalReturnTypes.map((rt) => getReturnTypeShortLabel(rt));

  const colorMap = getPersistentPaletteMap(
    "engagement-dashboard.shared-return-type.colors",
    returnTypeKeys,
    theme?.palette
  );

  const getCount = (partner: string, returnType: string) =>
    rows
      .filter((r) => r.PrimaryPartner === partner && r.ReturnType === returnType)
      .reduce((sum, r) => sum + safeNumber(r.TotalCount), 0);

  const getAmount = (partner: string, returnType: string) =>
    rows
      .filter((r) => r.PrimaryPartner === partner && r.ReturnType === returnType)
      .reduce((sum, r) => sum + safeCurrencyNumber(r.ForecastAmount), 0);

  const series: BarSeries[] = finalReturnTypes.map((returnType) => {
    const shortLabel = getReturnTypeShortLabel(returnType);

    return {
      type: "bar",
      name: shortLabel,
      color: colorMap[shortLabel],
      data: totals.map((partner) => ({
        y: getAmount(partner, returnType),
        custom: {
          count: getCount(partner, returnType),
          forecastAmount: getAmount(partner, returnType),
          returnType: shortLabel,
          partnerName: partner,
        },
      })),
    };
  });

  return {
    ...chartDefaults,
    chart: {
      type: "bar",
      marginRight: 50,
      spacingBottom: 20,
      events: {
        load: function () {
          setVisibleYAxisExtremes(this);
          bindCategoryAxisWheelScroll({
            chart: this,
            totalItems: totals.length,
            visibleCountDefault: 4,
            bindingKey: "__wheelBoundExecutivePartnerReturnType",
          });
        },
      },
    },
    title: { text: "Expected Revenue by Primary Partner" },
    xAxis: {
      categories: totals,
      title: { text: "Primary Partner" },
      gridLineWidth: 0,
      lineWidth: 0,
      tickWidth: 0,
      min: 0,
      max: Math.min(4, Math.max(totals.length - 1, 0)),
      events: {
        afterSetExtremes: function () {
          setVisibleYAxisExtremes(this.chart);
        },
      },
      scrollbar: buildStandardScrollbar({
        enabled: totals.length > 5,
        height: 10,
        margin: 8,
        minWidth: 30,
      }),
    },
    yAxis: {
      min: 0,
      endOnTick: false,
      maxPadding: 0.12,
      title: { text: "Revenue Amount ($)" },
      labels: {
        rotation: 0,
        style: {
          whiteSpace: "nowrap",
          fontSize: "11px",
        },
        formatter: function () {
          return formatChartCurrencyCompact(Number(this.value));
        },
      },
      tickPixelInterval: 90,
      gridLineWidth: 0,
      lineWidth: 0,
      stackLabels: {
        enabled: true,
        useHTML: true,
        x: 2,
        y: 0,
        overflow: "allow",
        crop: false,
        formatter: function () {
          const partner = totals[this.x as number];
          const total = partnerTotalsMap[partner];

          if (!total) {
            return `${formatChartCurrencyCompact(Number(this.total ?? 0))}`;
          }

          return `
            <div style="
              text-align: center;
              background: #ffffff;
              padding: 4px 8px;
              border-radius: 6px;
              min-width: 78px;
            ">
              <div style="font-size: 11px; font-weight: 600; margin-bottom: 2px;">
                ${formatChartCurrencyCompact(Number(this.total ?? 0))}
              </div>
              <div style="font-size: 10px; color: #6b7280;">
                ${safeNumber(total.count)} ELs
              </div>
            </div>
          `;
        },
      },
    },
    tooltip: {
      useHTML: true,
      borderWidth: 0,
      backgroundColor: "transparent",
      shadow: false,
      formatter: function () {
        try {
          const point = this.point as any;
          const partner = String(this.key ?? "");
          const amount = Number(this.y ?? 0);
          const count = Number(point?.custom?.count ?? 0);
          const returnType = String(point?.custom?.returnType ?? this.series.name ?? "");

          return `
            <div style="
              background: #ffffff;
              border: 1px solid #d9d9d9;
              border-radius: 8px;
              box-shadow: 0 2px 8px rgba(0,0,0,0.12);
              padding: 8px 10px;
              color: #333333;
              font-size: 12px;
            ">
              <div style="font-weight: 600; margin-bottom: 4px;">${partner}</div>
              <div>${returnType} Revenue: <b>${formatChartCurrencyCompact(amount)}</b></div>
              <div>${returnType} ELs: <b>${count}</b></div>
            </div>
          `;
        } catch (e) {
          return "";
        }
      },
    },
    plotOptions: {
      bar: {
        stacking: "normal",
        dataLabels: { enabled: false },
        pointWidth: 26,
        groupPadding: 0.22,
        pointPadding: 0.16,
        borderWidth: 1,
        borderColor: "#ffffff",
        events: {
          legendItemClick: function () {
            const chart = this.chart;
            setTimeout(() => setVisibleYAxisExtremes(chart), 0);
          },
        },
      },
    },
    legend: {
      enabled: true,
      align: "center",
      verticalAlign: "bottom",
      margin: 12,
      padding: 8,
    },
    series,
  } satisfies Highcharts.Options;
};


/**
 * Office x Status stacked column (column)
 * backend rows: [{ Office, ApprovalStatus, TotalCount }]
 */
export const buildOfficeStackedColumnOptions = (
  rows: OfficeStatusRow[],
  statuses: readonly string[] = FIXED_APPROVAL_STATUS_ORDER
): Highcharts.Options => {
  const offices = Array.from(new Set(rows.map((r) => r.Office))).filter(Boolean);

  const totals = offices
    .map((office) => ({
      office,
      total: rows
        .filter(
          (r) => r.Office === office && statuses.includes(r.ApprovalStatus as any)
        )
        .reduce((sum, r) => sum + safeNumber(r.TotalCount), 0),
    }))
    .sort((a, b) => b.total - a.total)
    .map((x) => x.office);

  const getVal = (office: string, status: string) =>
    rows
      .filter((r) => r.Office === office && r.ApprovalStatus === status)
      .reduce((sum, r) => sum + safeNumber(r.TotalCount), 0);

  const series: ColumnSeries[] = [...statuses].map((status) => ({
    type: "column",
    name: status,
    data: totals.map((office) => getVal(office, status)),
    color: STATUS_COLOR_MAP[status] || undefined,
  }));

  return {
    ...chartDefaults,
    chart: {
      type: "column",
      spacingBottom: 20,
      marginRight: 60,
      plotBorderColor: "#eef0f1",
      plotBorderWidth: 1,
      events: {
        load: function () {
          bindCategoryAxisWheelScroll({
            chart: this,
            totalItems: totals.length,
            visibleCountDefault: 4,
            bindingKey: "__wheelBoundELOffice",
          });
        },
      },
    },
    title: {
      text:
        statuses.length === 1
          ? "EL Distribution by Office (Approved)"
          : "EL Distribution by Office",
    },
    xAxis: {
      categories: totals,
      gridLineWidth: 0,
      lineWidth: 0,
      tickWidth: 0,
      title: { text: "Office" },
      labels: { style: { fontSize: "12px" } },
      min: 0,
      max: Math.min(4, Math.max(totals.length - 1, 0)),
      scrollbar: buildStandardScrollbar({
        enabled: totals.length > 5,
        height: 10,
        margin: 8,
        minWidth: 30,
      }),
    },
    yAxis: {
      min: 0,
      gridLineWidth: 1,
      title: { text: "Total Count" },
      allowDecimals: false,
      stackLabels: {
        enabled: true,
      },
      reversedStacks: false,
    },
    tooltip: {
      useHTML: true,
      borderWidth: 0,
      backgroundColor: "transparent",
      shadow: false,
      formatter: function () {
        try {
          const point = this.point as any;
          const office = String(this.x ?? "");
          const status = String(this.series.name ?? "");
          const count = Number(this.y ?? 0);
          const total = Number(point?.stackTotal ?? 0);

          return buildChartTooltipCard(office, [
            `${status}: <b>${count}</b>`,
            `Total: <b>${total}</b>`,
          ]);
        } catch (e) {
          return "";
        }
      },
    },
    plotOptions: {
      column: {
        stacking: "normal",
        dataLabels: { enabled: false },
        pointWidth: 28,
        pointPadding: 0.18,
        groupPadding: 0.2,
        borderWidth: 1,
        borderColor: "#ffffff",
      },
    },
    legend: {
      enabled: true,
      align: "center",
      verticalAlign: "bottom",
    },
    series,
  } satisfies Highcharts.Options;
};

/**
 * Office Forecast Amount (column)
 * backend rows: [{ Office, ForecastAmount, ELCount }]
 * - Shows total forecast amount by office
 */
export const buildOfficeForecastAmountColumnOptions = (
  rows: OfficeForecastAmountRow[],
  theme?: ThemeColors
): Highcharts.Options => {
  const offices = Array.from(new Set(rows.map((r) => r.Office))).filter(Boolean);

  const officeTotalsMap = offices.reduce<
    Record<string, { count: number; amount: number }>
  >((acc, office) => {
    const officeRows = rows.filter((r) => r.Office === office);
    acc[office] = {
      count: officeRows.reduce((sum, r) => sum + safeNumber(r.ELCount), 0),
      amount: officeRows.reduce(
        (sum, r) => sum + safeCurrencyNumber(r.ForecastAmount),
        0
      ),
    };
    return acc;
  }, {});

  const totals = offices
    .map((o) => ({
      o,
      totalCount: officeTotalsMap[o]?.count ?? 0,
      totalAmount: officeTotalsMap[o]?.amount ?? 0,
    }))
    .sort((a, b) => {
      if (b.totalAmount !== a.totalAmount) return b.totalAmount - a.totalAmount;
      return b.totalCount - a.totalCount;
    })
    .map((x) => x.o);

  const returnTypes = Array.from(new Set(rows.map((r) => r.ReturnType))).filter(Boolean);

  const orderedReturnTypes = [
    "1040 Individual",
    "1040-NR Individual",
    "1041 Fiduciary",
    "1065 Partnership",
    "1120 Corporation",
    "1120-S Corporation",
    "1120-F Corporation",
    "1120-H Corporation",
    "5500 Employee Benefit Plan",
    "706 Estate",
    "709 Gift",
    "990 Exempt Organization",
    "990-PF Private Foundation",
  ].filter((rt) => returnTypes.includes(rt));

  const remainingReturnTypes = returnTypes.filter(
    (rt) => !orderedReturnTypes.includes(rt)
  );

  const finalReturnTypes = [...orderedReturnTypes, ...remainingReturnTypes];

  const returnTypeKeys = finalReturnTypes.map((rt) => getReturnTypeShortLabel(rt));

  const colorMap = getPersistentPaletteMap(
    "engagement-dashboard.shared-return-type.colors",
    returnTypeKeys,
    theme?.palette
  );

  const getRow = (office: string, returnType: string) =>
    rows.find((r) => r.Office === office && r.ReturnType === returnType);

  const series: ColumnSeries[] = finalReturnTypes.map((returnType) => {
    const shortLabel = getReturnTypeShortLabel(returnType);

    return {
      type: "column",
      name: shortLabel,
      color: colorMap[shortLabel],
      data: totals.map((office) => {
        const row = getRow(office, returnType);
        return {
          y: safeCurrencyNumber(row?.ForecastAmount),
          custom: {
            count: safeNumber(row?.ELCount),
            forecastAmount: safeCurrencyNumber(row?.ForecastAmount),
            returnType: shortLabel,
            officeName: office,
          },
        };
      }),
    };
  });

  const setVisibleYAxisExtremes = (chart: Highcharts.Chart) => {
    try {
      const categoryAxis = chart.xAxis[0];
      const valueAxis = chart.yAxis[0];

      const minIndex = Math.max(0, Math.floor(Number(categoryAxis.min ?? 0)));
      const maxIndex = Math.min(
        Math.max(totals.length - 1, 0),
        Math.ceil(Number(categoryAxis.max ?? totals.length - 1))
      );

      let visibleMax = 0;

      for (let i = minIndex; i <= maxIndex; i++) {
        let stackTotal = 0;

        chart.series.forEach((series: any) => {
          if (series.visible) {
            const point = series.points?.[i];
            stackTotal += Number(point?.y ?? 0);
          }
        });

        visibleMax = Math.max(visibleMax, stackTotal);
      }

      const paddedMax = visibleMax > 0 ? visibleMax * 1.22 : 1;
      valueAxis.setExtremes(0, paddedMax, false, false);
      chart.redraw(false);
    } catch (e) {
      // no-op
    }
  };

  return {
    ...chartDefaults,
    chart: {
      type: "column",
      spacingBottom: 20,
      marginRight: 80,
      events: {
        load: function () {
          setVisibleYAxisExtremes(this);
          bindCategoryAxisWheelScroll({
            chart: this,
            totalItems: totals.length,
            visibleCountDefault: 4,
            bindingKey: "__wheelBoundExecutiveOffice",
          });
        },
      },
    },
    title: { text: "Expected Revenue by Office" },
    xAxis: {
      categories: totals,
      crosshair: true,
      gridLineWidth: 0,
      lineWidth: 0,
      tickWidth: 0,
      title: { text: "Office" },
      min: 0,
      max: Math.min(4, Math.max(totals.length - 1, 0)),
      scrollbar: buildStandardScrollbar({
        enabled: totals.length > 5,
        height: 10,
        margin: 8,
        minWidth: 30,
      }),
      events: {
        afterSetExtremes: function () {
          setVisibleYAxisExtremes(this.chart);
        },
      },
    },
    yAxis: {
      min: 0,
      maxPadding: 0.12,
      title: { text: "Revenue Amount ($)" },
      labels: {
        formatter: function () {
          return formatChartCurrencyCompact(Number(this.value));
        },
      },
      stackLabels: {
        enabled: true,
        useHTML: true,
        y: -9,
        formatter: function () {
          const office = totals[this.x as number];
          const total = officeTotalsMap[office];

          if (!total) {
            return `${formatChartCurrencyCompact(Number(this.total ?? 0))}`;
          }

          return `
            <div style="
              text-align: center;
              background: #ffffff;
              padding: 4px 8px;
              border-radius: 6px;
              min-width: 78px;
            ">
              <div style="font-size: 11px; font-weight: 600; margin-bottom: 2px;">
                ${formatChartCurrencyCompact(Number(this.total ?? 0))}
              </div>
              <div style="font-size: 10px; color: #6b7280;">
                ${safeNumber(total.count)} ELs
              </div>
            </div>
          `;
        },
      },
      gridLineWidth: 0,
      lineWidth: 0,
      reversedStacks: false,
    },
    tooltip: {
      useHTML: true,
      borderWidth: 0,
      backgroundColor: "transparent",
      shadow: false,
      formatter: function () {
        try {
          const point = this.point as any;
          const office = String(this.x ?? "");
          const amount = Number(point?.custom?.forecastAmount ?? 0);
          const returnType = String(point?.custom?.returnType ?? this.series.name ?? "");
          const count = Number(point?.custom?.count ?? 0);

          return `
            <div style="
              background: #ffffff;
              border: 1px solid #d9d9d9;
              border-radius: 8px;
              box-shadow: 0 2px 8px rgba(0,0,0,0.12);
              padding: 8px 10px;
              color: #333333;
              font-size: 12px;
            ">
              <div style="font-weight: 600; margin-bottom: 4px;">${office}</div>
              <div>${returnType} Revenue: <b>${formatChartCurrencyCompact(amount)}</b></div>
              <div>${returnType} ELs: <b>${count}</b></div>
            </div>
          `;
        } catch (e) {
          return "";
        }
      },
    },
    plotOptions: {
      column: {
        stacking: "normal",
        dataLabels: { enabled: false },
        pointWidth: 28,
        pointPadding: 0.18,
        groupPadding: 0.2,
        events: {
          legendItemClick: function () {
            const chart = this.chart;
            setTimeout(() => setVisibleYAxisExtremes(chart), 0);
          },
        },
      },
    },
    legend: {
      enabled: true,
      align: "center",
      verticalAlign: "bottom",
    },
    series,
  } satisfies Highcharts.Options;
};

/**
 * Return Type Summary (column + line)
 * backend rows: [{ ReturnType, COUNT, ForecastAmount }]
 * - Column = count
 * - Line = forecast amount
 */
export const buildReturnTypeSummaryComboOptions = (
  rows: ReturnTypeSummaryRow[],
  theme?: ThemeColors
): Highcharts.Options => {
  const desiredOrder = [
    "1040 Individual",
    "1040-NR Individual",
    "1041 Fiduciary",
    "1065 Partnership",
    "1120 Corporation",
    "1120-S Corporation",
    "1120-F Corporation",
    "1120-H Corporation",
    "5500 Employee Benefit Plan",
    "706 Estate",
    "709 Gift",
    "990 Exempt Organization",
    "990-PF Private Foundation",
  ];

  const normalized = rows
    .map((r) => ({
      ReturnType: String(r.ReturnType || "No Return Type"),
      ShortLabel: getReturnTypeShortLabel(r.ReturnType || "No Return Type"),
      TotalCount: safeNumber((r as any).TotalCount ?? (r as any).COUNT ?? 0),
      ForecastAmount: safeCurrencyNumber(r.ForecastAmount),
    }))
    .sort((a, b) => b.ForecastAmount - a.ForecastAmount);

  const ordered = [
    ...desiredOrder
      .map((rt) => normalized.find((r) => r.ReturnType === rt))
      .filter(Boolean),
    ...normalized.filter((r) => !desiredOrder.includes(r.ReturnType)),
  ] as Array<{
    ReturnType: string;
    ShortLabel: string;
    TotalCount: number;
    ForecastAmount: number;
  }>;

  const returnTypeKeys = ordered.map((r) => r.ShortLabel);

  const colorMap = getPersistentPaletteMap(
    "engagement-dashboard.shared-return-type.colors",
    returnTypeKeys,
    theme?.palette
  );

  return {
    ...chartDefaults,
    chart: {
      type: "column",
      spacingBottom: 20,
      plotBorderColor: "#eef0f1",
      plotBorderWidth: 1,
      events: {
        load: function () {
          bindCategoryAxisWheelScroll({
            chart: this,
            totalItems: ordered.length,
            visibleCountDefault: 4,
            bindingKey: "__wheelBoundReturnTypeCombo",
          });
        },
      },
    },
    title: { text: "Return Type Summary" },
    xAxis: {
      categories: ordered.map((r) => r.ShortLabel),
      crosshair: true,
      lineWidth: 0,
      tickWidth: 0,
      title: { text: "Return Type" },
      labels: {
        rotation: 0,
        style: { fontSize: "12px" },
      },
      min: 0,
      max: Math.min(4, Math.max(ordered.length - 1, 0)),
      scrollbar: buildStandardScrollbar({
        enabled: ordered.length > 5,
        height: 10,
        margin: 8,
        minWidth: 30,
      }),
    },
    yAxis: [
      {
        title: { text: "EL Count" },
        min: 0,
        allowDecimals: false,
      },
      {
        title: { text: "Revenue Amount ($)" },
        min: 0,
        opposite: true,
        labels: {
          formatter: function () {
            return `$${Highcharts.numberFormat(Number(this.value), 0)}`;
          },
        },
      },
    ],
    tooltip: {
      shared: true,
      useHTML: true,
      borderWidth: 0,
      backgroundColor: "transparent",
      shadow: false,
      formatter: function () {
        try {
          const points = this.points || [];
          const returnType = String(this.x ?? "");

          const countPoint = points.find(
            (p) => p.series.name === "EL Count"
          );

          const amountPoint = points.find(
            (p) => p.series.name === "Revenue Amount"
          );

          const count = Number(countPoint?.y ?? 0);
          const amount = Number(amountPoint?.y ?? 0);

          return `
            <div style="
              background: #ffffff;
              border: 1px solid #d9d9d9;
              border-radius: 8px;
              box-shadow: 0 2px 8px rgba(0,0,0,0.12);
              padding: 8px 10px;
              color: #333333;
              font-size: 12px;
            ">
              <div style="font-weight: 600; margin-bottom: 4px;">${returnType}</div>
              <div>Revenue: <b>${formatChartCurrencyCompact(amount)}</b></div>
              <div>ELs: <b>${count}</b></div>
            </div>
          `;
        } catch (e) {
          return "";
        }
      },
    },
    legend: {
      enabled: true,
      align: "center",
      verticalAlign: "bottom",
    },
    plotOptions: {
      column: {
        pointWidth: 34,
      },
      line: {
        marker: {
          enabled: true,
        },
      },
    },
    series: [
      {
        type: "column",
        name: "EL Count",
        yAxis: 0,
        data: ordered.map((r) => ({
          y: r.TotalCount,
          color: colorMap[r.ShortLabel],
        })),
      },
      {
        type: "line",
        name: "Revenue Amount",
        yAxis: 1,
        color: "#000000",
        marker: {
          enabled: true,
          fillColor: "#000000",
          lineColor: "#000000",
          lineWidth: 1.5,
          radius: 4,
        },
        data: ordered.map((r) => ({
          y: r.ForecastAmount,
          color: "#000000",
          marker: {
            fillColor: colorMap[r.ShortLabel],
            lineColor: "#000000",
            lineWidth: 1.5,
            radius: 4,
          },
        })),
      },
    ],
  } satisfies Highcharts.Options;
};

/**
 * Forecast amount by Line of Service (pie)
 * backend rows: [{ LineOfService, ForecastAmount, ELCount }]
 * - Shows forecast amount distribution by line of service
 */
export const buildLineOfServiceForecastAmountPieOptions = (
  rows: LineOfServiceForecastAmountRow[],
  theme?: ThemeColors
): Highcharts.Options => {
  const normalized = rows
    .map((r) => ({
      LineOfService: String(r.LineOfService || "No Line of Service")
        .toLowerCase()
        .replace(/\b\w/g, (c) => c.toUpperCase()),
      ELCount: safeNumber(r.ELCount),
      ForecastAmount: safeCurrencyNumber(r.ForecastAmount),
    }))
    .filter((r) => r.ForecastAmount > 0 || r.ELCount > 0)
    .sort((a, b) => b.ForecastAmount - a.ForecastAmount);

  const losKeys = normalized.map((r) => r.LineOfService);

  const colorMap = getPersistentPaletteMap(
    "engagement-dashboard.line-of-service-forecast.colors",
    losKeys,
    theme?.palette
  );

  const totalForecast = normalized.reduce(
    (sum, r) => sum + safeCurrencyNumber(r.ForecastAmount),
    0
  );

  const minimumVisibleShare = 0.01;

  const series: PieSeries[] = [
    {
      type: "pie",
      name: "Revenue Amount",
      data: normalized.map((r) => {
        const actualAmount = safeCurrencyNumber(r.ForecastAmount);
        const actualShare = totalForecast > 0 ? actualAmount / totalForecast : 0;

        return {
          name: r.LineOfService,
          y:
            actualAmount > 0 && actualShare < minimumVisibleShare
              ? totalForecast * minimumVisibleShare
              : actualAmount,
          color: colorMap[r.LineOfService],
          custom: {
            actualAmount,
            elCount: r.ELCount,
            actualShare,
          },
        };
      }),
    },
  ];

  return {
    ...chartDefaults,
    chart: { type: "pie" },
    title: { text: "Expected Revenue by Line of Service" },
    tooltip: {
      useHTML: true,
      borderWidth: 0,
      backgroundColor: "transparent",
      shadow: false,
      formatter: function () {
        try {
          const point = this.point as any;
          const lineOfService = String(point?.name ?? "");
          const amount = Number(
            point?.custom?.actualAmount ?? point?.y ?? 0
          );
          const elCount = Number(
            point?.custom?.elCount ?? 0
          );
          const revenueShare = Number(
            point?.custom?.actualShare != null
              ? point.custom.actualShare * 100
              : point?.percentage ?? 0
          );

          return `
            <div style="
              background: #ffffff;
              border: 1px solid #d9d9d9;
              border-radius: 8px;
              box-shadow: 0 2px 8px rgba(0,0,0,0.12);
              padding: 8px 10px;
              color: #333333;
              font-size: 12px;
            ">
              <div style="font-weight: 600; margin-bottom: 4px;">${lineOfService}</div>
              <div>Revenue: <b>${formatChartCurrencyCompact(amount)}</b></div>
              <div>Revenue Share: <b>${Highcharts.numberFormat(revenueShare, 1)}%</b></div>
              <div>ELs: <b>${elCount}</b></div>
            </div>
          `;
        } catch (e) {
          return "";
        }
      },
    },
    plotOptions: {
      pie: {
        allowPointSelect: true,
        cursor: "pointer",
        size: "80%",
        dataLabels: {
          enabled: true,
          formatter: function () {
            const point = this.point as any;
            const actualAmount = Number(point?.custom?.actualAmount ?? point?.y ?? 0);
            return `<b>${point.name}</b>: ${formatChartCurrencyCompact(actualAmount)}`;
          },
        },
        showInLegend: true,
      },
    },
    legend: {
      enabled: true,
      layout: "horizontal",
      align: "center",
      verticalAlign: "bottom",
    },
    series,
  } satisfies Highcharts.Options;
};